import { color } from './colors';
import { ColorSet, ColorSetColors, ComponentColorMode, ComponentColors } from './colors.types';

/**
 * A typed list of color sets, which is a subset of colors in our defined color list.
 * These sets should match the color sets defined in Figma.
 */
export const colorSets: Record<ColorSet, ColorSetColors> = {
  neutral: {
    ...color.neutralNew.surface,
  },
  black: {
    ...color.neutralNew.black,
  },
  white: {
    ...color.neutralNew.white,
  },
  primary: {
    ...color.hierarchy.primary,
  },
  secondary: {
    ...color.hierarchy.secondary,
  },
  tertiary: {
    ...color.hierarchy.tertiary,
  },
  ai: {
    ...color.product.ai,
  },
  negative: {
    ...color.message.negative,
  },
  positive: {
    ...color.message.positive,
  },
  warning: {
    ...color.message.warning,
  },
  active: {
    ...color.state.active,
  },
  disabled: {
    ...color.state.disabled,
  },
  focus: {
    ...color.utils.focus,
  },
  luminocityDark: {
    ...color.utils.luminocityDark,
  },
  luminocityLight: {
    ...color.utils.luminocityLight,
  },
  skeleton: {
    ...color.utils.skeleton,
  },
};

interface ComponentColorsOptions {
  disabled?: boolean;
  active?: boolean;
  /** Some color modes, like ai, uses a gradient as default fill value. This option allows you to use the solid fill color instead. */
  noGradient?: boolean;
}

/**
 * Returns the appropriate colors for a component based on its color mode, state and other options.
 * The returned colors are picked from a single color set,
 * except for the focus ring color which is agnostic to the color mode and state.
 */
export function getComponentColors(
  colorMode: ComponentColorMode,
  options: ComponentColorsOptions = {}
): ComponentColors {
  const { disabled, active, noGradient } = options;

  let colorSet: ColorSetColors;
  if (disabled) {
    colorSet = color.state.disabled;
  } else if (active && colorMode === 'neutral') {
    colorSet = color.state.active;
  } else {
    colorSet = colorSets[colorMode];
  }
  // Color sets that have a gradient, should use this as the default fill color.
  if (colorSet.fillGradient && !noGradient) {
    colorSet.fill = colorSet.fillGradient;
  }

  return {
    stroke: colorSet.stroke || 'inherit',
    on: colorSet.on || 'inherit',
    fill: colorSet.fill || 'inherit',
    strokeSubjacent: colorSet.strokeSubjacent || 'inherit',
    onSubjacent: colorSet.onSubjacent || 'inherit',
    fillSubjacent: colorSet.fillSubjacent || 'inherit',
    fillFeedback: colorSet.fillFeedback || 'inherit',
    // The focus ring color is always the same, regardless of the color mode.
    focusRing: color.utils.focus.stroke,
  };
}
